import { Directive, ElementRef, ViewChild } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { GooglePay } from '@df/module-google-pay/google-pay.namespace';
import { GooglePayService } from '@df/module-google-pay/google-pay.service';
import { NgInject } from '@df/ng/ng-inject';
import { IBasketData } from '@df/session/api/basket/basket';
import { NBarclaycardGooglePay } from './barclaycard-google-pay.namespace';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class BarclaycardGooglePayComponentDef extends AbstractCheckoutPayment<NBarclaycardGooglePay.IPaymentMethod> {
    methodCode = NBarclaycardGooglePay.methodCode;

    /**
     * Google pay validate status
     * @type {Status}
     */
    availableStatus = new Status();

    private googlePayPaymentsClient?: GooglePay.IPaymentsClientInstance;

    /**
     * Basket data for chekcout success event
     */
    private basketData: IBasketData;

    /**
     * checkout data for chekcout success event
     */
    private checkoutData: Checkout.ISubmissionData;

    /**
     * Google Pay Submit button container element
     */
    @ViewChild('barclaycardGooglePayButtonContainer')
    buttonContainer: ElementRef;

    /**
     * Google pay service
     */
    @NgInject(GooglePayService)
    private googlePayService: GooglePayService;

    ngOnInit(): void {
        super.ngOnInit();
        this.availableStatus.subscribe(() => this.detectChanges());
    }

    /////////////////////////////////////////////
    //
    // BUTTON ADDING
    //
    /////////////////////////////////////////////

    protected getJsAssets(): string[] {
        return [GooglePay.Scripts.pay];
    }

    protected onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();
        this.googlePayPaymentsClient = this.getPaymentsClient();
        if (this.googlePayPaymentsClient) {
            this.googlePayPaymentsClient.isReadyToPay(this.getIsReadyToPayRequest()).then(r => {
                if (!r.result) {
                    this.onAddButtonError();
                    return;
                }
                this.addButton();
            });
        } else {
            this.onAddButtonError();
        }
    }

    /**
     * Returns GooglePay payments client instance
     * @returns {GooglePay.IPaymentsClientInstance | undefined}
     */
    private getPaymentsClient(): GooglePay.IPaymentsClientInstance | undefined {
        return this.googlePayService.getPaymentsClientInstance({
            environment: this.getEnvironment(),
            paymentDataCallbacks: {
                // onPaymentDataChanged: e => this.onPaymentDataChanged(e),
                onPaymentAuthorized: e => this.onPaymentAuthorized(e)
            }
        });
    }

    private getEnvironment(): GooglePay.Environments {
        return this.method.isTest ? GooglePay.Environments.test : GooglePay.Environments.production;
    }

    private getIsReadyToPayRequest(): GooglePay.IIsReadyToPayRequest {
        return {
            apiVersion: NBarclaycardGooglePay.version,
            apiVersionMinor: NBarclaycardGooglePay.minorVersion,
            allowedPaymentMethods: [this.getBasePaymentMethod()]
        };
    }

    /**
     * Adds button as container element child (eceuted only once so no need to clear container)
     */
    private addButton(): void {
        const button = this.googlePayPaymentsClient.createButton({ onClick: () => this.submit() });
        this.buttonContainer.nativeElement.appendChild(button);
    }

    /**
     * On add button error
     */
    private onAddButtonError(): void {
        this.availableStatus.error = NBarclaycardGooglePay.notAvailableMessage;
    }

    /////////////////////////////////////////////
    //
    // BTTON CLICK / ORDER SUBMIT
    //
    /////////////////////////////////////////////

    /**
     * Method executed on "pay with Google Pay" button click
     */
    private submit(): void {
        if (this.checkoutFormComponent.isCheckoutValid && !this.checkoutSubmitStatus.busy) {
            // Save billing address first as we dont execute standard checkout submit with all the data so billing address might remained unsaved.
            ((<CheckoutFormComponentDef>this.checkoutFormComponent)?.saveBillingAddress?.() || (new Promise<void>(resolve => {
                    resolve();
                })))
                .then(() => {
                    this.basketData = this.app.customer.basket.data;
                    this.checkoutData = this.checkoutFormComponent.getSubmissionData();
                    this.app.request.send(
                        this.checkoutSubmitStatus,
                        { path: NBarclaycardGooglePay.EEndpoints.request },
                        (result: NBarclaycardGooglePay.IRequestSuccessResponse) => {
                            if (!result.success) {
                                return;
                            }
                            // Preparing payment data request data
                            const paymentDataRequest: GooglePay.IPaymentDataRequest = {
                                apiVersion: NBarclaycardGooglePay.version,
                                apiVersionMinor: NBarclaycardGooglePay.minorVersion,
                                merchantInfo: {
                                    merchantId: this.method.merchantId,
                                    merchantName: this.method.merchantName
                                },
                                allowedPaymentMethods: [
                                    {
                                        ...{
                                            tokenizationSpecification: {
                                                type: GooglePay.PaymentMethodTokenizationType.PAYMENT_GATEWAY,
                                                parameters: {
                                                    gateway: this.method.gatewayId,
                                                    gatewayMerchantId: this.method.gatewayMerchantId
                                                }
                                            }
                                        },
                                        ...this.getBasePaymentMethod()
                                    }
                                ],
                                transactionInfo: {
                                    ...result.request.total,
                                    countryCode: this.app.multisite.current.country
                                },
                                callbackIntents: [GooglePay.PaymentDataRequestIntent.PAYMENT_AUTHORIZATION],
                                shippingAddressRequired: false,
                                shippingOptionRequired: false
                            };
                            // Starting actual GooglePay payment modal
                            this.googlePayPaymentsClient.loadPaymentData(paymentDataRequest);
                        },
                        () => {
                            this.basketData = undefined;
                            this.checkoutData = undefined;
                        }
                    );
                })
                .catch(() => {});
        } else {
            this.checkoutFormComponent.validateCheckout();
        }
    }

    /**
     * Method executed once we get payment authorization form google pay.
     * @param {GooglePay.IPaymentData} paymentData:
     * @return {Promise<GooglePay.IPaymentAuthorizationResult>}
     */
    private onPaymentAuthorized(paymentData: GooglePay.IPaymentData): Promise<GooglePay.IPaymentAuthorizationResult> {
        return new Promise<GooglePay.IPaymentAuthorizationResult>((resolve, reject) => {
            this.app.request.send(
                this.checkoutSubmitStatus,
                {
                    path: NBarclaycardGooglePay.EEndpoints.order,
                    data: {
                        token: paymentData.paymentMethodData.tokenizationData.token,
                        email: paymentData.email
                    }
                },
                result => {
                    resolve({ transactionState: GooglePay.PaymentAuthorizationResultStatus.SUCCESS });

                    const eventDdata: Checkout.ISubmitSuccessEventData = {
                        request: this.checkoutData,
                        basket: this.basketData,
                        response: result
                    };

                    this.app.events.broadcast<Checkout.ISubmitSuccessEventData>(Checkout.EVENTS.SubmitSuccess, eventDdata);

                    if (!result.post_redirect && !result.external_redirect && !result.preventSuccess) {
                        // keep subbmission as busy, we are redirecting now
                        // (that will prevent empty basket redirect)
                        this.checkoutSubmitStatus.superBusy = true;
                        this.checkoutFormComponent.redirectToSuccess();
                    } else if (result.external_redirect) {
                        this.checkoutSubmitStatus.superBusy = true;
                    }
                },
                error => {
                    this.basketData = undefined;
                    this.checkoutData = undefined;
                    this.checkoutSubmitStatus.error = error.message;
                    reject({
                        transactionState: GooglePay.PaymentAuthorizationResultStatus.ERROR,
                        error: {
                            intent: GooglePay.PaymentDataRequestIntent.PAYMENT_AUTHORIZATION,
                            message: error.message,
                            reason: GooglePay.PaymentDataErrorReason.PAYMENT_DATA_INVALID
                        }
                    });
                }
            );
        });
    }

    /////////////////////////////////////////////
    //
    // HELPERS
    //
    /////////////////////////////////////////////

    /**
     * Initial payment method data
     * Its partial but needed for isReadyToPay request
     * @return {GooglePay.IPaymentMethod}
     */
    private getBasePaymentMethod(): GooglePay.IPaymentMethod {
        return {
            type: GooglePay.PaymentMethodType.CARD,
            parameters: {
                allowedAuthMethods: [
                    GooglePay.CardParametersAllowedAuthMethods.PAN_ONLY,
                    GooglePay.CardParametersAllowedAuthMethods.CRYPTOGRAM_3DS
                ],
                allowedCardNetworks: this.cardTypes,
                billingAddressRequired: false
            }
        };
    }

    /**
     * Allowed card types in google pay format
     */
    private get cardTypes(): GooglePay.TCardType[] {
        return this.method.allowedCardTypes || [];
    }
}
